import { useEffect, useState } from "react";
import "./EmployeeManagement.scss";
import {
  Avatar,
  Button,
  ConfigProvider,
  Layout,
  message,
  Popover,
  Skeleton,
  Table,
  Tag,
  theme,
  Tooltip,
} from "antd";
import SiderComponent from "../DashBoard/SiderComponent";
import ReusableHeader from "../../components/ReusableHeader/ReusableHeader";
import InviteEmployeeModal, {
  InviteEmployeeFormData,
  InviteSuccessInfo,
} from "../../components/InviteEmployeeModal/InviteEmployeeModal";
import DeleteEmployeeModal from "../../components/DeleteEmployeeModal/DeleteEmployeeModal";
import AgentLocationMapModal from "../../components/AgentLocationMapModal/AgentLocationMapModal";
import {
  isShopUser,
  isEmailBasedRole,
  isEitherContactRole,
  isShopStockManager,
} from "../../utils/roles";
import { getSession } from "../../utils/session";
import { logout } from "../../utils/logout";
import { Logout } from "../../assets/svgs/Logout";
import { useSelector } from "react-redux";
import profile from "../../assets/svgs/Profile.svg";
import {
  UserAddOutlined,
  TeamOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  GoogleOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import createNewStoreEmployeeService from "../../services/createNewStoreEmployee";
import updateStoreEmployeeService from "../../services/updateStoreEmployee";
import deleteStoreEmployeeService from "../../services/deleteStoreEmployee";
import { AllOrderManagementState } from "../../store/allOrders.slice";
import { useDispatch } from "react-redux";
import {
  fetchStoreEmployees,
  AllStoreEmployeeState,
} from "../../store/allStoreEmployee.slice";
import * as Sentry from "@sentry/react";
import AppFooter from "../../components/AppFooter";

const { Content } = Layout;

// API Response Types
interface LocationHistoryItem {
  latitude: string;
  longitude: string;
  createdAt: string;
  modifiedAt: string;
}

interface StoreEmployee {
  userId: number;
  shopId: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
  profilePicUrl: string | null;
  role:
    | "SHOP_PICKER"
    | "SHOP_DELIVERY"
    | "SHOP_SUPERVISOR"
    | "SHOP_CATALOG_EDITOR"
    | "SHOP_STOCK_MANAGER";
  config?: { allowedCategories?: string[] } | null;
  loginType?: LoginType | null;
  status: "Registered" | "Invited" | "Deleted";
  locationHistory?: LocationHistoryItem[];
}

type LoginType = "Email" | "Phone" | "Google";

// Component Types
interface SupervisorData {
  key: string;
  id: number;
  name: string;
  contact: string;
  contactType: "email" | "phone";
  loginType: LoginType | null;
  status: "Registered" | "Invited" | "Deleted";
}

interface LocationPoint {
  lat: number;
  lng: number;
  timestamp: string;
}

interface AgentData {
  key: string;
  id: number;
  name: string;
  role:
    | "SHOP_PICKER"
    | "SHOP_DELIVERY"
    | "SHOP_CATALOG_EDITOR"
    | "SHOP_STOCK_MANAGER";
  contact: string;
  contactType: "email" | "phone";
  loginType: LoginType | null;
  status: "Registered" | "Invited" | "Deleted";
  location: string; // Keep for backward compatibility - will be latest location
  locations?: LocationPoint[]; // Array of all locations for polyline
  updatedAt: string;
}

const EmployeeManagement = () => {
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<
    (SupervisorData | AgentData) | null
  >(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState<
    (SupervisorData | AgentData) | null
  >(null);
  const [isLocationMapVisible, setIsLocationMapVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentData | null>(null);
  // Holds the login-help info shown when a login-type icon in the table is
  // clicked. Reuses the invite modal's confirmation view in display mode.
  const [loginHelpInfo, setLoginHelpInfo] = useState<InviteSuccessInfo | null>(
    null
  );
  const shopId = useSelector(
    (state: { allOrderManagement: AllOrderManagementState }) =>
      state?.allOrderManagement?.shopId
  );
  const dispatch = useDispatch();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const firebaseLoginState = useSelector(
    (state: any) => state.login.firebaseLogin
  );
  const storeEmployeesData = useSelector(
    (state: { allStoreEmployee: AllStoreEmployeeState }) =>
      state.allStoreEmployee?.list?.data || null
  );

  const isLoadingEmployees = useSelector(
    (state: { allStoreEmployee: AllStoreEmployeeState }) =>
      state.allStoreEmployee?.list?.loading || false
  );

  useEffect(() => {
    if (shopId) {
      dispatch(fetchStoreEmployees({ shopId: shopId }));
    }
  }, [shopId, dispatch]);

  // Normalize the loginType coming from the API to the supported enum values
  const normalizeLoginType = (
    loginType?: string | null
  ): LoginType | null => {
    if (
      loginType === "Email" ||
      loginType === "Phone" ||
      loginType === "Google"
    ) {
      return loginType;
    }
    return null;
  };

  // Build the login-help info for a table row so the same confirmation modal
  // can explain how the employee logs in.
  const buildLoginHelpInfo = (
    record: SupervisorData | AgentData
  ): InviteSuccessInfo | null => {
    if (!record.loginType) return null;

    // Supervisors have no explicit role field; everyone else carries one.
    const role = "role" in record ? record.role : "SHOP_SUPERVISOR";

    let method: InviteSuccessInfo["method"];
    if (record.loginType === "Phone") {
      method = "phone";
    } else if (record.loginType === "Google") {
      method = "google";
    } else {
      method = "email";
    }

    return {
      name: record.name,
      role,
      method,
      email: record.contactType === "email" ? record.contact : undefined,
      phoneNumber: record.contactType === "phone" ? record.contact : undefined,
    };
  };

  const handleLoginTypeClick = (record: SupervisorData | AgentData) => {
    const info = buildLoginHelpInfo(record);
    if (info) {
      setLoginHelpInfo(info);
    }
  };

  // Render the login type as a clickable icon (only when a supported value is
  // present). Clicking opens the "how to login" confirmation.
  const renderLoginType = (record: SupervisorData | AgentData) => {
    const loginType = record.loginType;
    if (!loginType) {
      return <span className="employee-login-type-empty">-</span>;
    }

    const iconMap: Record<LoginType, JSX.Element> = {
      Google: <GoogleOutlined />,
      Phone: <PhoneOutlined />,
      Email: <MailOutlined />,
    };

    return (
      <Tooltip title={`${loginType} — click to view login steps`}>
        <button
          type="button"
          className="employee-login-type-cell employee-login-type-button"
          aria-label={`${loginType} login. View login steps.`}
          onClick={() => handleLoginTypeClick(record)}
        >
          {iconMap[loginType]}
        </button>
      </Tooltip>
    );
  };

  // Transform API data to component format
  const transformSupervisorData = (
    supervisor: StoreEmployee
  ): SupervisorData => {
    const contact = supervisor.email || supervisor.phoneNumber || "";
    const contactType: "email" | "phone" = supervisor.email ? "email" : "phone";

    return {
      key: supervisor.userId.toString(),
      id: supervisor.userId,
      name: supervisor.name,
      contact: contact,
      contactType: contactType,
      loginType: normalizeLoginType(supervisor.loginType),
      status: supervisor.status,
    };
  };

  const transformAgentData = (agent: StoreEmployee): AgentData => {
    const contact = agent.phoneNumber || agent.email || "";
    const contactType: "email" | "phone" = agent.phoneNumber
      ? "phone"
      : "email";

    // Transform locationHistory to LocationPoint format
    let locations: LocationPoint[] | undefined;
    let location = "";
    let updatedAt = "";

    if (agent.locationHistory && agent.locationHistory.length > 0) {
      // Sort by modifiedAt descending to get latest first
      const sortedHistory = [...agent.locationHistory].sort(
        (a, b) =>
          new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
      );

      const latestLocation = sortedHistory[0];
      location = `${latestLocation.latitude},${latestLocation.longitude}`;
      updatedAt = latestLocation.modifiedAt;

      // Transform all locations for polyline
      locations = sortedHistory.map((loc) => ({
        lat: parseFloat(loc.latitude),
        lng: parseFloat(loc.longitude),
        timestamp: loc.modifiedAt,
      }));
    }

    return {
      key: agent.userId.toString(),
      id: agent.userId,
      name: agent.name,
      role: agent.role as
        | "SHOP_PICKER"
        | "SHOP_DELIVERY"
        | "SHOP_CATALOG_EDITOR"
        | "SHOP_STOCK_MANAGER",
      contact: contact,
      contactType: contactType,
      loginType: normalizeLoginType(agent.loginType),
      status: agent.status,
      location: location,
      locations: locations,
      updatedAt: updatedAt,
    };
  };

  // Transform API response to component data
  const supervisorsData: SupervisorData[] = storeEmployeesData?.supervisors
    ? storeEmployeesData.supervisors.map(transformSupervisorData)
    : [];

  const agentsData: AgentData[] = storeEmployeesData?.storeEmployees
    ? storeEmployeesData.storeEmployees
        .filter(
          (emp) =>
            emp.role === "SHOP_DELIVERY" ||
            emp.role === "SHOP_PICKER" ||
            emp.role === "SHOP_CATALOG_EDITOR" ||
            emp.role === "SHOP_STOCK_MANAGER"
        )
        .map(transformAgentData)
    : [];

  const handleInviteEmployeeClick = () => {
    setEditingEmployee(null);
    setIsInviteModalVisible(true);
  };

  const handleEditEmployeeClick = (employee: SupervisorData | AgentData) => {
    setEditingEmployee(employee);
    setIsInviteModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsInviteModalVisible(false);
    setEditingEmployee(null);
  };

  const convertEmployeeToFormData = (
    employee: SupervisorData | AgentData
  ): InviteEmployeeFormData => {
    // Check if employee has a role property (AgentData) or not (SupervisorData)
    const role = "role" in employee ? employee.role : "SHOP_SUPERVISOR";

    // Get the original employee data from storeEmployeesData to access both email and phoneNumber
    const allEmployees = [
      ...(storeEmployeesData?.storeEmployees || []),
      ...(storeEmployeesData?.supervisors || []),
    ];
    const originalEmployee = allEmployees.find(
      (emp) => emp.userId === employee.id
    );

    const formData: InviteEmployeeFormData = {
      role: role as
        | "SHOP_DELIVERY"
        | "SHOP_SUPERVISOR"
        | "SHOP_PICKER"
        | "SHOP_CATALOG_EDITOR"
        | "SHOP_STOCK_MANAGER",
      fullName: employee.name,
    };

    // Only include email for supervisors, phoneNumber for agents
    if (isEitherContactRole(role)) {
      // For catalog editor, determine contact method from available data
      if (originalEmployee?.email) {
        formData.email = originalEmployee.email;
        formData.contactMethod = "email";
      } else if (originalEmployee?.phoneNumber) {
        formData.phoneNumber = originalEmployee.phoneNumber;
        formData.contactMethod = "phone";
      }
    } else if (isShopStockManager(role)) {
      // Stock managers use email and carry their allowed categories
      formData.email = originalEmployee?.email || undefined;
      formData.allowedCategories =
        originalEmployee?.config?.allowedCategories || undefined;
    } else if (isEmailBasedRole(role)) {
      formData.email = originalEmployee?.email || undefined;
    } else {
      // For agents, get phoneNumber from original employee data
      // Fallback to contact field if originalEmployee lookup fails
      const phoneNumber =
        originalEmployee?.phoneNumber ||
        (employee.contactType === "phone" ? employee.contact : undefined);
      formData.phoneNumber = phoneNumber || undefined;
    }

    return formData;
  };

  const handleInviteSubmit = async (values: InviteEmployeeFormData) => {
    if (editingEmployee) {
      // Update employee API call
      try {
        const updateData: any = {
          userId: editingEmployee.id,
          shopId: shopId || "",
          name: values.fullName,
        };

        // Only include email for supervisors and pickers, phoneNumber for agents
        if (isEitherContactRole(values.role)) {
          // For catalog editor, send whichever contact method was chosen
          if (values.contactMethod === "email") {
            updateData.email = values.email || "";
          } else {
            updateData.phoneNumber = values.phoneNumber || "";
          }
        } else if (isShopStockManager(values.role)) {
          // Stock manager update only carries the allowed categories config
          updateData.config = {
            allowedCategories: values.allowedCategories || [],
          };
        } else if (isEmailBasedRole(values.role)) {
          updateData.email = values.email || "";
        } else {
          updateData.phoneNumber = values.phoneNumber || "";
        }

        await updateStoreEmployeeService.updateStoreEmployee(updateData);
        message.success("Employee updated successfully");
        handleModalCancel();
        // Refresh the employee list after successful update
        if (shopId) {
          dispatch(fetchStoreEmployees({ shopId: shopId }));
        }
      } catch (error) {
        console.error("Error updating employee:", error);
        Sentry.captureException(error);
        message.error("Failed to update employee. Please try again.");
      }
    } else {
      // Here you would make the API call to invite the employee
      try {
        const inviteData: any = {
          shopId: shopId,
          name: values.fullName,
          role: values.role,
        };

        // Only include email for supervisors and pickers, phoneNumber for agents
        if (isEitherContactRole(values.role)) {
          // For catalog editor, send whichever contact method was chosen
          if (values.contactMethod === "email") {
            inviteData.email = values.email || "";
            // Email-capable invite carries loginType (Google / Email) and,
            // for Email, a password.
            if (values.loginType) {
              inviteData.loginType = values.loginType;
              if (values.loginType === "Email" && values.password) {
                inviteData.password = values.password;
              }
            }
          } else {
            inviteData.phoneNumber = values.phoneNumber || "";
          }
        } else if (isShopStockManager(values.role)) {
          // Stock manager is invited via email and with allowed categories
          inviteData.email = values.email || "";
          inviteData.config = {
            allowedCategories: values.allowedCategories || [],
          };
          if (values.loginType) {
            inviteData.loginType = values.loginType;
            if (values.loginType === "Email" && values.password) {
              inviteData.password = values.password;
            }
          }
        } else if (isEmailBasedRole(values.role)) {
          inviteData.email = values.email || "";
          // Don't include phoneNumber for supervisors
          // Email-capable invite carries loginType (Google / Email) and,
          // for Email, a password.
          if (values.loginType) {
            inviteData.loginType = values.loginType;
            if (values.loginType === "Email" && values.password) {
              inviteData.password = values.password;
            }
          }
        } else {
          inviteData.phoneNumber = values.phoneNumber || "";
          // Don't include email for agents
        }

        await createNewStoreEmployeeService.createNewStoreEmployee(inviteData);
        // Refresh the employee list after successful invite
        if (shopId) {
          dispatch(fetchStoreEmployees({ shopId: shopId }));
        }

        // Determine the invite channel so the modal can render the right
        // "account created" confirmation copy.
        let method: InviteSuccessInfo["method"];
        if (inviteData.phoneNumber) {
          method = "phone";
        } else if (inviteData.loginType === "Google") {
          method = "google";
        } else {
          method = "email";
        }

        // Returning the success info keeps the modal open and shows the
        // confirmation message instead of closing + toasting.
        return {
          name: values.fullName,
          role: values.role,
          method,
          email: inviteData.email,
          phoneNumber: inviteData.phoneNumber,
        } as InviteSuccessInfo;
      } catch (error) {
        console.error("Error inviting employee:", error);
        Sentry.captureException(error);
        message.error("Failed to invite employee. Please try again.");
      }
    }
  };

  const handleDeleteEmployeeClick = (employee: SupervisorData | AgentData) => {
    setDeletingEmployee(employee);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeletingEmployee(null);
  };

  const handleDeleteConfirm = async () => {
    if (deletingEmployee) {
      try {
        await deleteStoreEmployeeService.deleteStoreEmployee(
          deletingEmployee.id.toString()
        );
        message.success("Employee deleted successfully");
        handleDeleteCancel();
        // Refresh the employee list after successful deletion
        if (shopId) {
          dispatch(fetchStoreEmployees({ shopId: shopId }));
        }
      } catch (error) {
        console.error("Error deleting employee:", error);
        Sentry.captureException(error);
        message.error("Failed to delete employee. Please try again.");
      }
    }
  };

  const handleReinviteEmployee = async (
    employee: SupervisorData | AgentData
  ) => {
    try {
      // Check if employee has a role property (AgentData) or not (SupervisorData)
      const role = "role" in employee ? employee.role : "SHOP_SUPERVISOR";

      // Get the original employee data to access both email and phoneNumber
      const allEmployees = [
        ...(storeEmployeesData?.storeEmployees || []),
        ...(storeEmployeesData?.supervisors || []),
      ];
      const originalEmployee = allEmployees.find(
        (emp) => emp.userId === employee.id
      );

      // Prepare API data - only include email for supervisors, phoneNumber for agents
      const apiData: any = {
        shopId: shopId,
        name: employee.name,
        role: role as
          | "SHOP_DELIVERY"
          | "SHOP_SUPERVISOR"
          | "SHOP_PICKER"
          | "SHOP_CATALOG_EDITOR"
          | "SHOP_STOCK_MANAGER",
      };

      if (isEitherContactRole(role)) {
        // For catalog editor, send whichever contact is available
        if (originalEmployee?.email) {
          apiData.email = originalEmployee.email;
        } else if (originalEmployee?.phoneNumber) {
          apiData.phoneNumber = originalEmployee.phoneNumber;
        }
      } else if (isShopStockManager(role)) {
        // Reinvite (create) a stock manager with email and allowed categories
        apiData.email = originalEmployee?.email || "";
        apiData.config = {
          allowedCategories: originalEmployee?.config?.allowedCategories || [],
        };
      } else if (isEmailBasedRole(role)) {
        apiData.email = originalEmployee?.email || "";
      } else {
        apiData.phoneNumber = originalEmployee?.phoneNumber || "";
      }

      await createNewStoreEmployeeService.createNewStoreEmployee(apiData);
      message.success("Employee reinvited successfully");
      // Refresh the employee list after successful reinvite
      if (shopId) {
        dispatch(fetchStoreEmployees({ shopId: shopId }));
      }
    } catch (error) {
      console.error("Error reinviting employee:", error);
      Sentry.captureException(error);
      message.error("Failed to reinvite employee. Please try again.");
    }
  };

  const handleLocationClick = (agent: AgentData) => {
    // If agent has multiple locations, show map modal
    if (agent.locations && agent.locations.length > 0) {
      setSelectedAgent(agent);
      setIsLocationMapVisible(true);
    } else if (agent.location) {
      // Fallback to single location - open Google Maps
      const [lat, long] = agent.location
        .split(",")
        .map((coord) => coord.trim());
      if (lat && long && !isNaN(Number(lat)) && !isNaN(Number(long))) {
        window.open(`https://www.google.com/maps?q=${lat},${long}`, "_blank");
      }
    }
  };

  const handleLocationMapClose = () => {
    setIsLocationMapVisible(false);
    setSelectedAgent(null);
  };

  const formatTimeAgo = (timestamp: string): string => {
    if (!timestamp) return "N/A";
    const now = new Date();
    const updatedAt = new Date(timestamp);
    const diffInMs = now.getTime() - updatedAt.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) {
      return "just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hr ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    } else {
      const diffInWeeks = Math.floor(diffInDays / 7);
      return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
    }
  };

  const currentUserRole = getSession("role");
  const isSupervisorRole = currentUserRole === "SHOP_SUPERVISOR";

  const supervisorColumns: ColumnsType<SupervisorData> = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <div className="employee-name-cell">
          <div className="employee-icon-wrapper">
            <TeamOutlined className="employee-icon" />
          </div>
          <div>
            <div className="employee-name">{text}</div>
          </div>
        </div>
      ),
    },
    {
      title: "CONTACT",
      dataIndex: "contact",
      key: "contact",
      render: (text: string, record: SupervisorData) => (
        <div className="employee-contact-cell">
          {record.contactType === "email" ? (
            <MailOutlined />
          ) : (
            <PhoneOutlined />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "LOGIN TYPE",
      dataIndex: "loginType",
      key: "loginType",
      render: (_: LoginType | null, record) => renderLoginType(record),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status === "Registered"
              ? "green"
              : status === "Invited"
                ? "orange"
                : "red"
          }
          className="employee-status-tag"
        >
          {status}
        </Tag>
      ),
    },
    ...(isSupervisorRole
      ? []
      : [
          {
            title: "ACTIONS",
            key: "actions",
            render: (_: any, record: SupervisorData) => {
              if (record.status === "Deleted") {
                return (
                  <ConfigProvider
                    theme={{ token: { colorPrimary: "#4526a0" } }}
                  >
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleReinviteEmployee(record)}
                      className="reinvite-employee-button"
                    >
                      Reinvite
                    </Button>
                  </ConfigProvider>
                );
              }
              return (
                <div className="employee-actions-cell">
                  <EditOutlined
                    className="employee-edit-icon"
                    onClick={() => handleEditEmployeeClick(record)}
                  />
                  <DeleteOutlined
                    className="employee-delete-icon"
                    onClick={() => handleDeleteEmployeeClick(record)}
                  />
                </div>
              );
            },
          },
        ]),
  ];
  const agentColumns: ColumnsType<AgentData> = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <div className="employee-name-cell">
          <div className="employee-icon-wrapper">
            <TeamOutlined className="employee-icon" />
          </div>
          <div>
            <div className="employee-name">{text}</div>
          </div>
        </div>
      ),
    },
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      render: (
        role:
          | "SHOP_PICKER"
          | "SHOP_DELIVERY"
          | "SHOP_CATALOG_EDITOR"
          | "SHOP_STOCK_MANAGER"
      ) => {
        const roleDisplay: string =
          role === "SHOP_DELIVERY"
            ? "Delivery Agent"
            : role === "SHOP_PICKER"
              ? "Picker"
              : role === "SHOP_CATALOG_EDITOR"
                ? "Catalog Editor"
                : role === "SHOP_STOCK_MANAGER"
                  ? "Stock Manager"
                  : "";
        return <span className="employee-role-cell">{roleDisplay}</span>;
      },
    },
    {
      title: "EMAIL/PHONE NUMBER",
      dataIndex: "contact",
      key: "contact",
      render: (text: string, record: AgentData) => (
        <div className="employee-contact-cell">
          {record.contactType === "email" ? (
            <MailOutlined />
          ) : (
            <PhoneOutlined />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "LOGIN TYPE",
      dataIndex: "loginType",
      key: "loginType",
      render: (_: LoginType | null, record) => renderLoginType(record),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status === "Registered"
              ? "green"
              : status === "Invited"
                ? "orange"
                : "red"
          }
          className="employee-status-tag"
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "LOCATION",
      dataIndex: "location",
      key: "location",
      render: (location: string, record: AgentData) => {
        const hasLocations = record.locations && record.locations.length > 0;
        const isValidLocation = location && location.includes(",");
        let lat: string | undefined;
        let long: string | undefined;

        if (location) {
          [lat, long] = location.split(",").map((coord) => coord.trim());
        }

        const canViewMap =
          hasLocations ||
          (isValidLocation &&
            lat &&
            long &&
            !isNaN(Number(lat)) &&
            !isNaN(Number(long)));
        const isDeleted = record.status === "Deleted";
        const isClickable = canViewMap && !isDeleted;

        return (
          <div
            className="employee-location-cell"
            onClick={() => isClickable && handleLocationClick(record)}
            style={{
              cursor: isClickable ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              opacity: isDeleted ? 0.5 : 1,
            }}
          >
            <EnvironmentOutlined
              style={{ color: isClickable ? "#4caf50" : "#999" }}
            />
            <span>
              {record.updatedAt
                ? formatTimeAgo(record.updatedAt)
                : "Not available"}
            </span>
          </div>
        );
      },
    },
    {
      title: "ACTIONS",
      key: "actions",
      render: (_: any, record: AgentData) => {
        if (record.status === "Deleted") {
          return (
            <ConfigProvider theme={{ token: { colorPrimary: "#4526a0" } }}>
              <Button
                size="small"
                type="primary"
                onClick={() => handleReinviteEmployee(record)}
                className="reinvite-employee-button"
              >
                Reinvite
              </Button>
            </ConfigProvider>
          );
        }
        return (
          <div className="employee-actions-cell">
            <EditOutlined
              className="employee-edit-icon"
              onClick={() => handleEditEmployeeClick(record)}
            />
            <DeleteOutlined
              className="employee-delete-icon"
              onClick={() => handleDeleteEmployeeClick(record)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Layout className="employee-management-main">
      <SiderComponent />
      <Layout>
        <ReusableHeader
          id="employee-header"
          style={{ background: colorBgContainer }}
          containerClassName="employee-header-main"
          rightSection={
            <div className="employee-profile">
              {isShopUser(getSession("role")) ? (
                <Popover
                  placement="bottomRight"
                  trigger="click"
                  content={
                    <div
                      onClick={logout}
                      style={{
                        minWidth: 100,
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <Logout />
                      <button
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "none",
                          background: "none",
                          color: "#d4380d",
                          fontWeight: 500,
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  }
                >
                  <Avatar
                    src={firebaseLoginState.data?.photoURL || profile}
                    className="avatarDiv"
                    style={{ cursor: "pointer" }}
                  />
                </Popover>
              ) : (
                <Avatar
                  src={firebaseLoginState.data?.photoURL || profile}
                  className="avatarDiv"
                />
              )}
            </div>
          }
        />
        <Content id="employee-content">
          <div className="employee-management-content-heading">
            <div className="employee-management-content-heading-title-container">
              <p className="employee-management-content-heading-title">
                Employee Management
              </p>
            </div>
            <div>
              <ConfigProvider theme={{ token: { colorPrimary: "#4526a0" } }}>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleInviteEmployeeClick}
                  className="invite-employee-button"
                >
                  Invite Employee
                </Button>
              </ConfigProvider>
            </div>
          </div>
          <div className="employee-management-content-stats">
            <div className="employee-stat-card employee-stat-card-agents">
              <div className="employee-stat-icon-wrapper">
                <TeamOutlined className="employee-stat-icon employee-stat-icon-agents" />
              </div>
              <div className="employee-stat-content">
                <p className="employee-stat-label">Agents</p>
                {isLoadingEmployees ? (
                  <Skeleton.Input
                    active
                    style={{
                      width: "40px",
                      height: "32px",
                    }}
                  />
                ) : (
                  <p className="employee-stat-value">
                    {storeEmployeesData?.totalAgents || 0}
                  </p>
                )}
              </div>
            </div>
            <div className="employee-stat-card employee-stat-card-supervisors">
              <div className="employee-stat-icon-wrapper">
                <TeamOutlined className="employee-stat-icon employee-stat-icon-supervisors" />
              </div>
              <div className="employee-stat-content">
                <p className="employee-stat-label">Supervisors</p>
                {isLoadingEmployees ? (
                  <Skeleton.Input
                    active
                    style={{
                      width: "40px",
                      height: "32px",
                    }}
                  />
                ) : (
                  <p className="employee-stat-value">
                    {storeEmployeesData?.totalSupervisors || 0}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="employee-management-tables">
            <div className="employee-table-section">
              <div className="employee-table-header">
                <TeamOutlined className="employee-table-header-icon" />
                <p className="employee-table-title">Supervisors</p>
              </div>
              {isLoadingEmployees ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={supervisorColumns}
                  dataSource={supervisorsData}
                  pagination={false}
                  className="employee-table custom-table-header"
                  rowClassName={(record) =>
                    record.status === "Deleted" ? "employee-row-deleted" : ""
                  }
                />
              )}
            </div>
            <div className="employee-table-section">
              <div className="employee-table-header">
                <TeamOutlined className="employee-table-header-icon" />
                <p className="employee-table-title">Agents</p>
              </div>
              {isLoadingEmployees ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={agentColumns}
                  dataSource={agentsData}
                  pagination={false}
                  className="employee-table custom-table-header"
                  rowClassName={(record) =>
                    record.status === "Deleted" ? "employee-row-deleted" : ""
                  }
                />
              )}
            </div>
          </div>
        </Content>
        <AppFooter />
      </Layout>
      <InviteEmployeeModal
        open={isInviteModalVisible}
        onCancel={handleModalCancel}
        onSubmit={handleInviteSubmit}
        initialData={
          editingEmployee
            ? convertEmployeeToFormData(editingEmployee)
            : undefined
        }
        title={editingEmployee ? "Update Employee" : "Invite New Employee"}
        submitButtonText={
          editingEmployee ? "Update Employee" : "Invite Employee"
        }
      />
      <InviteEmployeeModal
        open={!!loginHelpInfo}
        onCancel={() => setLoginHelpInfo(null)}
        onSubmit={() => undefined}
        displaySuccessInfo={loginHelpInfo}
      />
      <DeleteEmployeeModal
        open={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        employeeName={deletingEmployee?.name || ""}
      />
      {selectedAgent && (
        <AgentLocationMapModal
          open={isLocationMapVisible}
          onCancel={handleLocationMapClose}
          agentName={selectedAgent.name}
          locations={selectedAgent.locations || []}
        />
      )}
    </Layout>
  );
};

export default EmployeeManagement;
